import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Marker, Polygon } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Plus,
  Flame,
  CheckCircle2,
  Pencil,
  Trash2,
  Eye,
  SlidersHorizontal,
  List as ListIcon,
  Map as MapIcon,
} from 'lucide-react';
import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { Section } from '@/components/primitives/Section';
import { DataList, type DataListColumn } from '@/components/primitives/DataList';
import { EmptyState } from '@/components/primitives/EmptyState';
import { MapCanvas } from '@/components/maps/MapCanvas';
import { RoleGuard } from '@/components/primitives/RoleGuard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  listWildfiresForMap,
  listWildfireSeasons,
  deleteWildfire,
} from '@/api/wildfires';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useFpaMapHome } from '@/hooks/useFpaMapHome';
import { LongPressPin } from '@/components/maps/LongPressPin';
import { WildfireDetailDrawer } from '@/components/maps/WildfireDetailDrawer';
import { PinPropertyAction } from '@/components/maps/PinPropertyAction';
import {
  wildfireColors,
  wildfireMarkerPosition,
  wildfirePolygonPathOptions,
  wildfirePolygonPositions,
} from '@/utils/wildfireMapStyle';
import { useMobileLayout } from '@/utils/useMobileLayout';
import { cn } from '@/lib/utils';

const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
const activeIcon = L.icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

interface Wildfire {
  id: number;
  fire_reference?: string | null;
  type?: string | null;
  is_active?: boolean;
  is_public?: boolean;
  review_status?: string | null;
  start_time?: string | null;
  suburb?: string | null;
  address?: string | null;
  area_size_hectares?: number | null;
  location?: { coordinates: [number, number] } | null;
  area_polygon?: { coordinates: [number, number][][] } | null;
}

interface MapListResponse {
  wildfires: Wildfire[];
  total: number;
  fire_season: string | null;
}

interface SeasonsResponse {
  seasons: string[];
  current_season: string | null;
  has_seasons: boolean;
}

// Sentinel passed via the season picker to mean "do not filter by season".
const SEASON_ALL = '__all__';

function formatDateTime(s: string | null | undefined): string {
  if (!s) return 'N/A';
  return new Date(s).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function WildfiresIndex() {
  const { isSuperAdmin } = useAuth();
  const { isMobile } = useMobileLayout();
  // Bulk select is a desktop-only affordance — the checkbox column and the
  // sticky action bar take up too much room on small screens, and the typical
  // mobile flow is single-row triage rather than multi-select cleanup.
  const canBulkSelect = isSuperAdmin() && !isMobile;
  const { tenantId } = useTenant();
  const mapHome = useFpaMapHome(tenantId ?? null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const view = (searchParams.get('view') === 'map' ? 'map' : 'list') as 'list' | 'map';

  // `wildfires` now holds the FULL un-paginated result for the current filter
  // set. The list view paginates client-side from this; the map renders the
  // whole thing so every fire shows up at once.
  const [wildfires, setWildfires] = useState<Wildfire[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isActive, setIsActive] = useState<string>('all');
  const [isPublic, setIsPublic] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Season picker. `season` is the currently-selected fire-season tag (e.g.
  // "2024/2025"), or SEASON_ALL to bypass the season filter. We seed it from
  // /wildfires/seasons on first load so the page defaults to the current
  // season — switching FPAs (tenantId change) re-seeds.
  const [season, setSeason] = useState<string>(SEASON_ALL);
  const [seasonOptions, setSeasonOptions] = useState<string[]>([]);
  const [currentSeason, setCurrentSeason] = useState<string | null>(null);
  const [hasSeasons, setHasSeasons] = useState(false);
  const [seasonsLoaded, setSeasonsLoaded] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<Wildfire | null>(null);

  // Bulk delete (super-admin only). `selectedIds` is the Set DataList needs;
  // `bulkConfirmOpen` gates the confirmation modal; `bulkDeleting` flips on
  // during the parallel-DELETE pass so the action bar can show progress.
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Tapping a marker opens a bottom drawer instead of a Leaflet popup —
  // the popup clipped its own content and buried the actions on a phone.
  const [drawerFire, setDrawerFire] = useState<Wildfire | null>(null);

  const fetchWildfires = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {};
      // Status (active/resolved/draft) is filtered client-side from the full
      // result so we can distinguish DRAFT (review_status) from the fire
      // lifecycle (is_active) without extra round-trips.
      if (isPublic !== 'all') params.is_public = isPublic === 'public';
      if (typeFilter !== 'all') params.type = typeFilter;
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate).toISOString();
      if (tenantId) params.tenant_id = tenantId;
      if (season !== SEASON_ALL) params.fire_season = season;

      const data = (await listWildfiresForMap(params)) as unknown as MapListResponse;
      setWildfires(data.wildfires || []);
    } catch (err) {
      console.error('Failed to list wildfires:', err);
      setError('Failed to load wildfires. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isPublic, typeFilter, startDate, endDate, tenantId, season]);

  useEffect(() => {
    fetchWildfires();
  }, [fetchWildfires]);

  // Load the season list for the current FPA. Default the picker to the
  // current season when the tenant has fires for it; otherwise show "All"
  // so the user doesn't land on an empty page. Re-runs when tenantId
  // changes so switching FPAs picks up that tenant's seasons.
  useEffect(() => {
    let cancelled = false;
    setSeasonsLoaded(false);
    (async () => {
      try {
        const params: Record<string, unknown> = {};
        if (tenantId) params.tenant_id = tenantId;
        const data = (await listWildfireSeasons(params)) as unknown as SeasonsResponse;
        if (cancelled) return;
        setSeasonOptions(data.seasons || []);
        setCurrentSeason(data.current_season ?? null);
        setHasSeasons(Boolean(data.has_seasons));
        // Default: current season if the tenant has fires for it, else "All".
        const currentInList =
          data.current_season && (data.seasons || []).includes(data.current_season);
        setSeason(currentInList ? (data.current_season as string) : SEASON_ALL);
      } catch (err) {
        // Non-fatal: seasons picker just won't show options.
        console.error('Failed to load wildfire seasons:', err);
        if (!cancelled) {
          setSeasonOptions([]);
          setHasSeasons(false);
          setSeason(SEASON_ALL);
        }
      } finally {
        if (!cancelled) setSeasonsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Whenever the underlying set or filters change, reset to page 1 so the
  // user isn't stranded on a page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [wildfires.length, pageSize]);

  // Client-side pagination over the un-paginated result. The map ignores
  // these and renders every wildfire.
  // Client-side status filter: DRAFT (review_status) vs the active/resolved
  // lifecycle (is_active). Drafts are excluded from active/resolved views.
  const statusFiltered = useMemo(
    () =>
      wildfires.filter((w) => {
        const isDraft = w.review_status === 'draft';
        if (isActive === 'draft') return isDraft;
        if (isActive === 'active') return !isDraft && Boolean(w.is_active);
        if (isActive === 'resolved') return !isDraft && !w.is_active;
        return true;
      }),
    [wildfires, isActive],
  );

  const total = statusFiltered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pagedWildfires = useMemo(
    () => statusFiltered.slice((page - 1) * pageSize, page * pageSize),
    [statusFiltered, page, pageSize],
  );

  const setView = (next: 'list' | 'map') => {
    const sp = new URLSearchParams(searchParams);
    sp.set('view', next);
    setSearchParams(sp, { replace: true });
  };

  const hasFilters =
    isActive !== 'all' ||
    isPublic !== 'all' ||
    typeFilter !== 'all' ||
    startDate ||
    endDate;
  const resetFilters = () => {
    setIsActive('all');
    setIsPublic('all');
    setTypeFilter('all');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };


  const onDelete = async () => {
    if (!pendingDelete) return;
    const w = pendingDelete;
    setPendingDelete(null);
    try {
      await deleteWildfire(w.id);
      toast.success(`Wildfire #${w.id} deleted`);
      fetchWildfires();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete wildfire');
    }
  };

  const onBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkConfirmOpen(false);
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    // Parallel DELETE — the per-row endpoint already enforces the tenant
    // guard, and super-admin bypasses it server-side. We Promise.allSettled
    // so a single failure doesn't abort the rest.
    const results = await Promise.allSettled(
      ids.map((id) => deleteWildfire(Number(id))),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    if (failed === 0) {
      toast.success(
        `Deleted ${ok} wildfire${ok === 1 ? '' : 's'}`,
      );
    } else if (ok === 0) {
      toast.error(`Failed to delete ${failed} wildfire${failed === 1 ? '' : 's'}`);
    } else {
      toast.warning(
        `Deleted ${ok}, failed ${failed}. Refreshing list.`,
      );
    }
    // Drop any IDs that successfully deleted from the selection so a partial
    // failure leaves the user with just the unresolved ones still ticked.
    const stillSelected = new Set<string | number>();
    ids.forEach((id, idx) => {
      if (results[idx]?.status === 'rejected') stillSelected.add(id);
    });
    setSelectedIds(stillSelected);
    setBulkDeleting(false);
    fetchWildfires();
  };

  const columns = useMemo<DataListColumn<Wildfire>[]>(
    () => [
      {
        id: 'ref',
        header: 'Ref',
        sortable: true,
        accessorFn: (w) => w.fire_reference || w.id,
        cell: (w) => (
          <span className="font-mono text-sm">{w.fire_reference || `#${w.id}`}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (w) =>
          w.review_status === 'draft' ? (
            <Badge variant="outline" className="gap-1 border-amber-400 text-amber-700">
              <Pencil className="h-3 w-3" /> Draft
            </Badge>
          ) : w.is_active ? (
            <Badge variant="destructive" className="gap-1">
              <Flame className="h-3 w-3" /> Active
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3" /> Resolved
            </Badge>
          ),
      },
      {
        id: 'start_time',
        header: 'Start time',
        sortable: true,
        accessorFn: (w) => w.start_time,
        cell: (w) => formatDateTime(w.start_time),
        hideBelow: 'md',
      },
      {
        id: 'suburb',
        header: 'Suburb',
        cell: (w) => w.suburb || 'N/A',
        hideBelow: 'lg',
      },
      {
        id: 'address',
        header: 'Address',
        cell: (w) => w.address || 'N/A',
        hideBelow: 'lg',
      },
      {
        id: 'area',
        header: 'Area (ha)',
        sortable: true,
        accessorFn: (w) => w.area_size_hectares ?? 0,
        cell: (w) => w.area_size_hectares?.toLocaleString() ?? 'N/A',
      },
      {
        id: 'visibility',
        header: 'Visibility',
        hideBelow: 'lg',
        cell: (w) =>
          w.is_public ? (
            <Badge>Public</Badge>
          ) : (
            <Badge variant="outline">Private</Badge>
          ),
      },
      {
        id: 'actions',
        header: '',
        cell: (w) => (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Button asChild size="icon" variant="ghost" title="View">
              <Link to={`/wildfires/${w.id}`}>
                <Eye className="h-4 w-4" />
              </Link>
            </Button>
            <RoleGuard staff>
              <Button asChild size="icon" variant="ghost" title="Edit">
                <Link to={`/wildfires/${w.id}/edit`}>
                  <Pencil className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => setPendingDelete(w)}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </RoleGuard>
          </div>
        ),
      },
    ],
    [],
  );

  const mobileCard = (w: Wildfire) => (
    <Card>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          {w.review_status === 'draft' ? (
            <Badge variant="outline" className="gap-1 border-amber-400 text-amber-700">
              <Pencil className="h-3 w-3" /> Draft
            </Badge>
          ) : w.is_active ? (
            <Badge variant="destructive" className="gap-1">
              <Flame className="h-3 w-3" /> Active
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3" /> Resolved
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            {formatDateTime(w.start_time)}
          </span>
        </div>
        <div className="font-mono text-base font-semibold">
          {w.fire_reference || `#${w.id}`}
        </div>
        <div className="text-sm text-muted-foreground">
          {w.suburb || 'Unknown suburb'} ·{' '}
          {w.area_size_hectares?.toLocaleString() ?? 'N/A'} ha
          {w.is_public ? ' · Public' : ' · Private'}
        </div>
        <RoleGuard staff>
          <div className="flex gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
            <Button asChild size="sm" variant="outline" className="flex-1">
              <Link to={`/wildfires/${w.id}/edit`}>
                <Pencil className="mr-1 h-3 w-3" /> Edit
              </Link>
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-destructive hover:text-destructive"
              onClick={() => setPendingDelete(w)}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
          </div>
        </RoleGuard>
      </CardContent>
    </Card>
  );

  return (
    <PageShell>
      <PageHeader
        title="Wildfires"
        subtitle={
          total > 0
            ? season === SEASON_ALL
              ? `${total} total · all seasons`
              : `${total} total · ${season} season${
                  season === currentSeason ? ' (current)' : ''
                }`
            : undefined
        }
        actions={
          <>
            <Tabs value={view} onValueChange={(v) => setView(v as 'list' | 'map')}>
              <TabsList>
                <TabsTrigger value="list">
                  <ListIcon className="mr-2 h-4 w-4" />
                  List
                </TabsTrigger>
                <TabsTrigger value="map">
                  <MapIcon className="mr-2 h-4 w-4" />
                  Map
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <RoleGuard staff>
              <Button asChild>
                <Link to="/wildfires/new">
                  <Plus className="mr-1 h-4 w-4" />
                  Report
                </Link>
              </Button>
            </RoleGuard>
          </>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Season picker — only renders once the FPA has fires for at least
          one season. Defaults to the current season; "All seasons" shows
          everything. Sits above the rest of the filters because the user
          almost always cares about *which* season they're viewing. */}
      {seasonsLoaded && hasSeasons && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-border bg-card px-3 py-2">
          <Label htmlFor="season-picker" className="text-sm font-medium">
            Season
          </Label>
          <Select value={season} onValueChange={setSeason}>
            <SelectTrigger id="season-picker" className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SEASON_ALL}>All seasons</SelectItem>
              {seasonOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                  {s === currentSeason ? ' · current' : ''}
                </SelectItem>
              ))}
              {/* Surface the current season even if no fires exist for it
                  yet, so the user can opt into the empty default explicitly. */}
              {currentSeason && !seasonOptions.includes(currentSeason) && (
                <SelectItem value={currentSeason}>
                  {currentSeason} · current (no fires yet)
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          {currentSeason && season !== currentSeason && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSeason(currentSeason)}
            >
              Back to current
            </Button>
          )}
        </div>
      )}

      <Section
        title="Filters"
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              {showFilters ? 'Hide' : 'Show'}
            </Button>
            {hasFilters && (
              <Button variant="outline" size="sm" onClick={resetFilters}>
                Reset
              </Button>
            )}
          </>
        }
      >
        {showFilters ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="filter-status">Status</Label>
              <Select value={isActive} onValueChange={(v) => { setIsActive(v); setPage(1); }}>
                <SelectTrigger id="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filter-visibility">Visibility</Label>
              <Select value={isPublic} onValueChange={(v) => { setIsPublic(v); setPage(1); }}>
                <SelectTrigger id="filter-visibility">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filter-type">Type</Label>
              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
                <SelectTrigger id="filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="veld">Veld</SelectItem>
                  <SelectItem value="structural">Structural</SelectItem>
                  <SelectItem value="rubbish">Rubbish</SelectItem>
                  <SelectItem value="permit_burn">Permit burn</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filter-start">Start from</Label>
              <Input
                id="filter-start"
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="filter-end">Start to</Label>
              <Input
                id="filter-end"
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {hasFilters
              ? 'Filters applied — click Show to edit.'
              : 'No filters applied.'}
          </p>
        )}
      </Section>

      {view === 'map' ? (
        <MapCanvas
          center={mapHome.center}
          zoom={mapHome.zoom}
          variant="lg"
          tenantId={tenantId ?? null}
          mapSearch
          /* Open framed on the user's FPA. Fits once per mount — a filter or
             season refetch leaves the user's own pan/zoom alone. */
          fitBoundsTo={mapHome.fitBoundsTo}
        >
          {({ settings }) => (
            <>
              <LongPressPin
                actions={(pin) => (
                  <PinPropertyAction pin={pin} enabled={settings.showParcels} />
                )}
              />
              {statusFiltered.map((w) => {
                const marker = wildfireMarkerPosition(w);
                if (!marker) return null;
                const [lat, lng] = marker;
                const polygon = wildfirePolygonPositions(w);
                const isDraft = w.review_status === 'draft';
                const { stroke: colorStroke } = wildfireColors(w);
                return (
                  <div key={w.id} className={cn('contents')}>
                    {settings.showPins && (
                      <Marker
                        position={[lat, lng]}
                        icon={w.is_active ? activeIcon : defaultIcon}
                        eventHandlers={{ click: () => setDrawerFire(w) }}
                      />
                    )}
                    {settings.showPolygons && polygon && polygon.length > 0 && (
                      <Polygon positions={polygon} pathOptions={wildfirePolygonPathOptions(w)} />
                    )}
                  </div>
                );
              })}
            </>
          )}
        </MapCanvas>
      ) : wildfires.length === 0 && !loading ? (
        <EmptyState
          icon={Flame}
          title={hasFilters ? 'No wildfires match your filters' : 'No wildfires yet'}
          description={
            hasFilters
              ? 'Try adjusting or resetting the filters.'
              : 'When wildfires are reported they will appear here.'
          }
          action={
            hasFilters ? (
              <Button variant="outline" onClick={resetFilters}>
                Reset filters
              </Button>
            ) : (
              <RoleGuard staff>
                <Button asChild>
                  <Link to="/wildfires/new">
                    <Plus className="mr-2 h-4 w-4" />
                    Report wildfire
                  </Link>
                </Button>
              </RoleGuard>
            )
          }
        />
      ) : (
        <>
          {/* Bulk-action bar — visible only when super admin has rows ticked.
              Sticks under the page header so it's easy to find while
              scrolling a long list. */}
          {canBulkSelect && selectedIds.size > 0 && (
            <div className="sticky top-14 z-20 mb-3 flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 shadow-sm">
              <div className="text-sm font-medium">
                {selectedIds.size} selected
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedIds(new Set())}
                  disabled={bulkDeleting}
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setBulkConfirmOpen(true)}
                  disabled={bulkDeleting}
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  {bulkDeleting
                    ? 'Deleting…'
                    : `Delete ${selectedIds.size}`}
                </Button>
              </div>
            </div>
          )}
          <DataList<Wildfire>
            data={pagedWildfires}
            columns={columns}
            mobileCard={mobileCard}
            loading={loading}
            getRowId={(w) => w.id}
            onRowClick={(w) => navigate(`/wildfires/${w.id}`)}
            selectable={canBulkSelect}
            selectedIds={canBulkSelect ? selectedIds : undefined}
            onSelectionChange={canBulkSelect ? setSelectedIds : undefined}
            pagination={{
              page,
              pageSize,
              total,
              totalPages,
              onPageChange: setPage,
              onPageSizeChange: (n) => {
                setPageSize(n);
                setPage(1);
              },
            }}
          />
        </>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this wildfire?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>
                  Wildfire {pendingDelete.fire_reference || `#${pendingDelete.id}`} will
                  be permanently removed. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk-delete confirmation (super admin only). */}
      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedIds.size} wildfire
              {selectedIds.size === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              All {selectedIds.size} selected record
              {selectedIds.size === 1 ? '' : 's'} will be permanently removed,
              including any attached photos, burn areas, and weather data. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={bulkDeleting}
            >
              {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WildfireDetailDrawer
        open={Boolean(drawerFire)}
        onOpenChange={(o) => { if (!o) setDrawerFire(null); }}
        wildfire={drawerFire}
        onViewDetails={(id) => navigate(`/wildfires/${id}`)}
        tenantId={tenantId ?? null}
        formatDateTime={formatDateTime}
      />
    </PageShell>
  );
}
